import {
  Assessment as AssessmentIcon,
  Assignment as AssignmentIcon,
  ThumbDown as ThumbDownIcon,
  ThumbUp as ThumbUpIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import AnalistaRoute from "../../components/AnalistaRoute";
import api from "../../services/api";

const Relatorios = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodoSelecionado, setPeriodoSelecionado] = useState(7);

  const [solicitacoesPorTipo, setSolicitacoesPorTipo] = useState([]);
  const [solicitacoesPorStatus, setSolicitacoesPorStatus] = useState([]);
  const [solicitacoesPorPeriodo, setSolicitacoesPorPeriodo] = useState([]);

  const [relatorioApi, setRelatorioApi] = useState(null);

  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
    "#8884d8",
    "#82ca9d",
    "#ffc658",
  ];

  const statusMap = {
    AguardandoAnalise: {
      label: "Aguardando Análise",
      color: "info",
    },
    EmAnalise: { label: "Em Análise", color: "warning" },
    SolicitandoInformacoes: {
      label: "Solicitando Informações",
      color: "info",
    },
    AguardandoPagamento: {
      label: "Aguardando Pagamento",
      color: "warning",
    },
    Aprovada: { label: "Aprovada", color: "success" },
    Rejeitada: { label: "Rejeitada", color: "error" },
  };

  const atualizarDadosFiltrados = async () => {
    try {
      setLoading(true);

      const response = await api.get("Relatorio", {
        params: {
          periodo: periodoSelecionado,
        },
      });
      setRelatorioApi(response.data.data);
      setError(null);
    } catch (err) {
      setError(
        "Não foi possível aplicar os filtros. Por favor, tente novamente.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (periodoSelecionado) {
      atualizarDadosFiltrados();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodoSelecionado]);

  useEffect(() => {
    if (relatorioApi?.quantidadePorStatus) {
      const dataForChart = Object.entries(relatorioApi.quantidadePorStatus).map(
        ([status, quantidade]) => {
          const statusInfo = statusMap[status] || { label: status };
          return {
            name: statusInfo.label,
            value: quantidade,
          };
        },
      );
      setSolicitacoesPorStatus(dataForChart);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relatorioApi]);

  useEffect(() => {
    if (relatorioApi?.quantidadePorTipoLicenca) {
      const dataForChart = Object.entries(
        relatorioApi.quantidadePorTipoLicenca,
      ).map(([tipo, quantidade]) => ({
        name: tipo,
        value: quantidade,
      }));
      setSolicitacoesPorTipo(dataForChart);
    }
  }, [relatorioApi]);

  const montarDescricaoGrafico = (itens, chaveValor = "value") =>
    itens.length
      ? itens.map((item) => `${item.name}: ${item[chaveValor]}`).join("; ")
      : "Sem dados";

  useEffect(() => {
    if (relatorioApi?.quantidadePorMes) {
      const meses = [
        "Janeiro",
        "Fevereiro",
        "Março",
        "Abril",
        "Maio",
        "Junho",
        "Julho",
        "Agosto",
        "Setembro",
        "Outubro",
        "Novembro",
        "Dezembro",
      ];
      const dataForChart = Object.entries(relatorioApi.quantidadePorMes).map(
        ([mes, quantidade]) => ({
          name: meses[parseInt(mes) - 1] || mes,
          solicitacoes: quantidade,
        }),
      );
      setSolicitacoesPorPeriodo(dataForChart);
    }
  }, [relatorioApi]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Carregando dados estatísticos...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Tentar novamente
        </Button>
      </Container>
    );
  }

  return (
    <AnalistaRoute>
      <Container
        maxWidth="lg"
        sx={{
          mt: { xs: 2, md: 4 },
          mb: { xs: 2, md: 4 },
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            mb: { xs: 3, md: 4 },
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <AssessmentIcon
            sx={{
              fontSize: { xs: 28, md: 32 },
              mr: { xs: 1, md: 2 },
              color: theme.palette.primary.main,
            }}
          />
          <Typography
            variant="h4"
            component="h1"
            fontWeight="bold"
            color="primary.main"
            sx={{ fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2.125rem" } }}
          >
            Dashboard de Relatórios
          </Typography>
        </Box>

        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 2 }}>
          <Typography
            variant="h6"
            sx={{ mb: 2, fontSize: { xs: "1.1rem", md: "1.25rem" } }}
          >
            Filtros
          </Typography>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Período</InputLabel>
                <Select
                  value={periodoSelecionado}
                  label="Período"
                  onChange={(e) => setPeriodoSelecionado(e.target.value)}
                >
                  <MenuItem value={7}>Últimos 7 dias</MenuItem>
                  <MenuItem value={30}>Últimos 30 dias</MenuItem>
                  <MenuItem value={90}>Últimos 90 dias</MenuItem>
                  <MenuItem value={365}>Último ano (365 dias)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {relatorioApi && (
          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <AssignmentIcon
                      sx={{ color: theme.palette.primary.main, mr: 1 }}
                    />
                    <Typography variant="h6" component="div">
                      Total de Solicitações
                    </Typography>
                  </Box>
                  <Typography
                    variant="h3"
                    component="div"
                    fontWeight="bold"
                    color="primary.main"
                    sx={{ fontSize: { xs: "2rem", md: "3rem" } }}
                  >
                    {relatorioApi?.quantidadePorStatus
                      ? Object.values(relatorioApi.quantidadePorStatus).reduce(
                          (acc, valor) => acc + valor,
                          0,
                        )
                      : 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <ThumbUpIcon
                      sx={{ color: theme.palette.success.main, mr: 1 }}
                    />
                    <Typography variant="h6" component="div">
                      Aprovadas
                    </Typography>
                  </Box>
                  <Typography
                    variant="h3"
                    component="div"
                    fontWeight="bold"
                    color="success.main"
                    sx={{ fontSize: { xs: "2rem", md: "3rem" } }}
                  >
                    {relatorioApi?.quantidadePorStatus?.Aprovada || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Taxa de aprovação:{" "}
                    {relatorioApi?.quantidadePorStatus
                      ? Math.round(
                          ((relatorioApi.quantidadePorStatus.Aprovada || 0) /
                            Object.values(
                              relatorioApi.quantidadePorStatus,
                            ).reduce((a, b) => a + b, 0)) *
                            100,
                        )
                      : 0}
                    %
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <ThumbDownIcon
                      sx={{ color: theme.palette.error.main, mr: 1 }}
                    />
                    <Typography variant="h6" component="div">
                      Rejeitadas
                    </Typography>
                  </Box>
                  <Typography
                    variant="h3"
                    component="div"
                    fontWeight="bold"
                    color="error.main"
                    sx={{ fontSize: { xs: "2rem", md: "3rem" } }}
                  >
                    {relatorioApi?.quantidadePorStatus?.Rejeitada || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Taxa de rejeição:{" "}
                    {relatorioApi?.quantidadePorStatus
                      ? Math.round(
                          ((relatorioApi.quantidadePorStatus.Rejeitada || 0) /
                            Object.values(
                              relatorioApi.quantidadePorStatus,
                            ).reduce((a, b) => a + b, 0)) *
                            100,
                        )
                      : 0}
                    %
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: { xs: 2, md: 3 },
                height: { xs: 300, sm: 350, md: 400 },
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}
              >
                Solicitações por Tipo de Licença
              </Typography>
              <Box
                role="img"
                aria-label={`Gráfico de solicitações por tipo de licença. ${montarDescricaoGrafico(solicitacoesPorTipo)}`}
                sx={{ height: "85%" }}
              >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={solicitacoesPorTipo}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill={theme.palette.primary.main}
                    dataKey="value"
                    nameKey="name"
                  >
                    {solicitacoesPorTipo.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [
                      `${value} solicitações`,
                      "Quantidade",
                    ]}
                    labelFormatter={(name) => `Tipo: ${name}`}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: { xs: 2, md: 3 },
                height: { xs: 300, sm: 350, md: 400 },
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}
              >
                Solicitações por Status
              </Typography>
              <Box
                role="img"
                aria-label={`Gráfico de solicitações por status. ${montarDescricaoGrafico(solicitacoesPorStatus)}`}
                sx={{ height: "85%" }}
              >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={solicitacoesPorStatus}
                  layout="horizontal"
                  margin={{ top: 5, right: 30, left: 5, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" type="category" tick={false} />
                  <YAxis type="number" />
                  <Tooltip
                    formatter={(value) => [
                      `${value} solicitações`,
                      "Quantidade",
                    ]}
                  />
                  <Bar dataKey="value" fill={theme.palette.primary.main} />
                </BarChart>
              </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper
              sx={{
                p: { xs: 2, md: 3 },
                height: { xs: 300, sm: 350, md: 400 },
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}
              >
                Evolução de Solicitações nos Últimos 12 Meses
              </Typography>
              <Box
                role="img"
                aria-label={`Gráfico de evolução de solicitações nos últimos 12 meses. ${montarDescricaoGrafico(solicitacoesPorPeriodo, "solicitacoes")}`}
                sx={{ height: "85%" }}
              >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={solicitacoesPorPeriodo}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip
                    formatter={(value) => [
                      `${value} solicitações`,
                      "Quantidade",
                    ]}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="solicitacoes"
                    stroke={theme.palette.primary.main}
                    activeDot={{ r: 8 }}
                    name="Solicitações"
                  />
                </LineChart>
              </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </AnalistaRoute>
  );
};

export default Relatorios;
